import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power } from 'lucide-react';
import { locationsApi } from '../../../api/admin/locations';
import { genericDocumentApi } from '../../../api/generic-documents';
import { ApiError } from '../../../api/client';
import { PageHeader } from '../../../components/PageHeader';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { WorkflowTrackingTab } from '../../../components/WorkflowTrackingTab';
import { LOCATION_STAGES } from '../../../config/workflow-stages';
import { WorkspaceTabs } from '../../../components/WorkspaceTabs';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

export function LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const locationId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('admin.locations.manage');

  const listQuery = useQuery({ queryKey: ['locations'], queryFn: () => locationsApi.list() });
  const docsQuery = useQuery({ queryKey: ['location-documents', locationId], queryFn: () => genericDocumentApi.listFor('location', locationId) });

  const deactivateMutation = useMutation({
    mutationFn: () => locationsApi.deactivate(locationId),
    onSuccess: () => { showSuccess('Location deactivated.'); queryClient.invalidateQueries({ queryKey: ['locations'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate location.'),
  });

  if (!listQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const l = listQuery.data.data.find((loc) => loc.id === locationId);
  if (!l) return <div className="page"><p className="muted">Location not found.</p></div>;

  return (
    <div className="page">
      <Link to="/admin/locations">&larr; Back to Locations</Link>
      <PageHeader
        title={l.name}
        actions={canManage && l.is_active && <button className="btn-secondary" onClick={() => deactivateMutation.mutate()}><Power size={16} className="inline-icon" /> Deactivate</button>}
        readOnly={!canManage}
      />
      <p className="muted">{l.level} · {l.is_active ? 'Active' : 'Inactive'}</p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: <CommentsThread entityType="location" entityId={locationId} />,
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('location', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('location', locationId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('location', documentId)}
                queryKeyToInvalidate={['location-documents', locationId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="location_hierarchy" entityId={locationId} actionStageMap={LOCATION_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
